import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { FaUserPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ROLES = [
  'editor_in_chief',
  'editor',
  'reviewer',
  'admin', // admin last
];

const ROLE_LABELS = {
  editor_in_chief: 'Editor in Chief',
  admin: 'Admin',
  editor: 'Editor',
  reviewer: 'Reviewer',
};

export default function EditorialBoardPage({ journalType }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', email: '', institution: '', expertise: '', experience: '' });
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const testPdfUrl = 'https://rbvgtaqimzpsarvoxubn.supabase.co/storage/v1/object/public/pdf/test.pdf';

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line
  }, [journalType]);

  async function fetchMembers() {
    setLoading(true);
    const { data } = await supabase
      .from('editorial_board')
      .select('*')
      .eq('journal_type', journalType.toLowerCase());
    setMembers(data || []);
    setLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStep(2);
  }

  async function handleReviewSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    await supabase.from('editorial_board_submissions').insert([
      {
        ...form,
        review_text: reviewText,
        pdf_url: testPdfUrl,
      },
    ]);
    setSubmitting(false);
    setSuccess(true);
    setStep(1);
    setForm({ name: '', email: '', institution: '', expertise: '', experience: '' });
    setReviewText('');
    setShowModal(false);
  }

  // Group members by role
  const grouped = ROLES.reduce((acc, role) => {
    acc[role] = members.filter(m => (m.role || '').toLowerCase() === role);
    return acc;
  }, {});

  return (
    <div className="container mt-5" style={{maxWidth: 1000}}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-4">Editorial Board - {journalType.toUpperCase()}</h2>
        <button className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2" onClick={() => navigate('/editorial-board/apply')}><FaUserPlus /> Join our Editorial Board as a Reviewer</button>
      </div>
      {showModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.18)'}} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content rounded-4">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold text-primary">Join as Reviewer</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                {step === 1 ? (
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Full Name</label>
                      <input type="text" className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Institution</label>
                      <input type="text" className="form-control" value={form.institution} onChange={e => setForm(f => ({ ...f, institution: e.target.value }))} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Expertise</label>
                      <input type="text" className="form-control" value={form.expertise} onChange={e => setForm(f => ({ ...f, expertise: e.target.value }))} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Experience</label>
                      <textarea className="form-control" rows={3} value={form.experience} onChange={e => setForm(f => ({ ...f, experience: e.target.value }))} required />
                    </div>
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-primary rounded-pill px-4">Next: Review Test PDF</button>
                      <button type="button" className="btn btn-secondary rounded-pill px-4" onClick={() => setShowModal(false)}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleReviewSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Test PDF (Review this document)</label>
                      <iframe src={testPdfUrl} title="Test PDF" style={{width: '100%', height: 400, border: '1px solid #ccc', borderRadius: 8}} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Write your review of the above PDF</label>
                      <textarea className="form-control" rows={6} value={reviewText} onChange={e => setReviewText(e.target.value)} required />
                    </div>
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-success rounded-pill px-4" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Review'}</button>
                      <button type="button" className="btn btn-secondary rounded-pill px-4" onClick={() => setShowModal(false)}>Cancel</button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </div>
      )}
      {success && (
        <div className="alert alert-success mt-4">Your application and review have been submitted! The editorial team will contact you soon.</div>
      )}
      {loading ? (
        <div className="text-center my-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
      ) : (
        ROLES.map(role => (
          <div key={role} className="mb-5">
            <h3 className="mb-3">{ROLE_LABELS[role]}</h3>
            {grouped[role].length === 0 ? (
              <div className="alert alert-info">No {ROLE_LABELS[role]} found for this journal.</div>
            ) : (
              <div className="">
                {grouped[role].map(member => (
                  <div className="card shadow-sm border-0 rounded-4 mb-3 w-100" key={member.id}>
                    <div className="card-body d-flex flex-row align-items-center gap-2 p-3" style={{minHeight: '90px'}}>
                      <div className="px-2 flex-fill"><h5 className="fw-bold mb-0">{member.name}</h5></div>
                      <div className="px-2 flex-fill text-secondary">{member.organization}</div>
                      <div className="px-2 flex-fill"><span className="badge bg-info bg-opacity-25 text-info">{ROLE_LABELS[role]}</span></div>
                      {member.country && <div className="px-2 flex-fill"><b>Country:</b> {member.country}</div>}
                      {member.bio && <div className="px-2 flex-fill"><b>Bio:</b> {member.bio}</div>}
                      {member.research_interests && <div className="px-2 flex-fill"><b>Research Interests:</b> {member.research_interests}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
} 